/* eslint-disable @next/next/no-img-element */
import { useRef, useState, useEffect } from 'react';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { Prarthana, PurposePrarthana } from '@/globalTypes';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider/Slider';
import { Skeleton } from '@mui/material';
import { ubuntu } from '@/globalConstants';


const AudioPlayer = ({ src, updateCurrentPrarthana, prarthanaDetails, type, title, maxWidth, maxHeight }: { src: string, updateCurrentPrarthana: any, prarthanaDetails: Prarthana | PurposePrarthana, type: string, title?: string, maxWidth?: string, maxHeight?: string }) => {
    const audioRef = useRef<any>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const volumeAtMute = useRef<any>(1);
    const [showVolumeSlider, setShowVolumeSlider] = useState(false);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        audio.addEventListener('timeupdate', updateTime);
        audio.addEventListener('loadedmetadata', updateDuration);

        return () => {
            audio.removeEventListener('timeupdate', updateTime);
            audio.removeEventListener('loadedmetadata', updateDuration);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioRef.current]);

    useEffect(() => {
        if (currentTime === duration && audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.pause();
            setIsPlaying(false);
        }
    }, [currentTime, duration]);
    const updateTime = () => {
        setCurrentTime(audioRef.current.currentTime);
    };

    const updateDuration = () => {
        setDuration(audioRef.current.duration);
    };

    const handleVolumeChange = (event: any, newValue: any) => {
        const audio = audioRef.current;
        setVolume(newValue);
        audio.volume = newValue;
        if (newValue === 0) {
            handleMuteBotton();
        }
        if (newValue && audioRef.current.muted) {
            handleMuteBotton();
        }
    };

    const formatTime = (time: number) => {
        const hours = Math.floor(time / 3600);
        const minutes = Math.floor((time % 3600) / 60);
        const seconds = Math.floor(time % 60);
        return `${hours > 0 ? `${hours}:` : ''}${minutes < 10 ? `0${minutes}:` : `${minutes}:`}${seconds < 10 ? `0${seconds}` : seconds}`;
    };
    const handleMuteBotton = () => {
        if (!audioRef.current.muted) {
            audioRef.current.muted = true;
            volumeAtMute.current = volume;
            setVolume(0);
        } else {
            audioRef.current.muted = false;
            setVolume(volumeAtMute.current);
        }
    }


    return (
        <>
            <div
                className={`audio-player flex items-center gap-3 px-4 py-3 ${isPlaying ? "bg-[#FDECCA] border-[#EE9322] border" : "bg-[#FFF9EB]"} rounded-2xl shadow-sm w-full max-w-xl min-h-[72px] h-[90px] transition-all duration-300`}
                style={{ maxWidth: maxWidth || '545px', minHeight: '72px', height: maxHeight || '90px' }}
            >
                {/* Image Left */}
                <div className="shrink-0 flex items-center">
                    <img
                        src={(() => {
                            if ((prarthanaDetails as any)?.image_url) return (prarthanaDetails as any).image_url;
                            if ((prarthanaDetails as any)?.album_art) return (prarthanaDetails as any).album_art;
                            if ((prarthanaDetails as any)?.ui_info?.image_url) return (prarthanaDetails as any).ui_info.image_url;
                            if ((prarthanaDetails as any)?.ui_info?.album_art) return (prarthanaDetails as any).ui_info.album_art;
                            return "/no-image.png";
                        })()}
                        alt={prarthanaDetails?.title || 'Prarthana'}
                        className="w-16 h-16 rounded-lg object-cover border border-[#F6C756] bg-white"
                        style={{ minWidth: '64px', minHeight: '64px', maxWidth: '64px', maxHeight: '64px' }}
                        onError={e => { (e.target as HTMLImageElement).src = "/no-image.png"; }}
                        loading="lazy"
                    />
                </div>

                {/* Details & Controls Center */}
                <div className="flex flex-col flex-1 min-w-0">
                    <div className="flex items-center gap-3 pb-2">
                        {/* Title */}
                        <div className={`${ubuntu.className} font-semibold text-base text-[#24282B] leading-tight max-w-full break-words`}>
                            {title || prarthanaDetails?.title || <Skeleton variant="text" width={140} />}
                        </div>
                    </div>
                    {/* Progress & Time */}
                    <div className="flex items-center gap-2 w-full">
                        {/* Play/Pause Button */}
                        {duration ? (
                            <IconButton>
                                {isPlaying ? (
                                    <PauseIcon sx={{ color: '#EE9322', fontSize: 32 }} onClick={() => { updateCurrentPrarthana(audioRef.current, "pause", prarthanaDetails, setIsPlaying) }} />
                                ) : (
                                    <PlayArrowIcon sx={{ color: '#EE9322', fontSize: 32 }} onClick={() => { updateCurrentPrarthana(audioRef.current, "play", prarthanaDetails, setIsPlaying) }} />
                                )}
                            </IconButton>
                        ) : (
                            <Skeleton variant="circular" width={40} height={40} />
                        )}
                        <div className="text-[#888] text-md font-medium" style={{ minWidth: 73 }}>
                            {duration ? `${formatTime(currentTime)}` : <Skeleton variant="rounded" height={16} width={40} />}
                            {duration ? `/ ${formatTime(duration)}` : <Skeleton variant="rounded" height={16} width={40} />}
                        </div>
                        <div className="flex-1 px-1">
                            <audio ref={audioRef} src={src} controls={false} />
                            <div
                                className="w-full h-2 bg-[#FFE7BA] rounded-full relative cursor-pointer"
                                onClick={(event: any) => {
                                    const rect = event.currentTarget.getBoundingClientRect();
                                    const clickX = event.clientX - rect.left;
                                    audioRef.current.currentTime = (clickX / rect.width) * duration;
                                }}
                            >
                                {/* Progress bar */}
                                <div
                                    className="h-full bg-[#EE9322] rounded-full"
                                    style={{ width: `${(currentTime / duration) * 100}%` }}
                                />
                                {isPlaying && <div
                                    className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-[#EE9322] rounded-full shadow-md"
                                    style={{
                                        left: `calc(${(currentTime / duration) * 100}% - 8px)`,
                                    }}
                                />}
                            </div>
                        </div>
                        {/* Volume Control */}
                        <div
                            className="flex items-center justify-center ml-4 group relative"
                            onMouseEnter={() => setShowVolumeSlider(true)}
                            onMouseLeave={() => setShowVolumeSlider(false)}
                        >
                            <div onClick={handleMuteBotton} className="cursor-pointer flex items-center">
                                {volume ? <VolumeUpIcon sx={{ color: isPlaying ? '#EE9322' : '#757575', fontSize: 22 }} /> : <VolumeOffIcon sx={{ color: isPlaying ? '#EE9322' : '#757575', fontSize: 22 }} />}
                            </div>
                            {/* Volume Slider only on hover (desktop) */}
                            {showVolumeSlider && (
                                <div className="absolute right-6 w-24 bg-white px-2 py-2 rounded shadow-lg border z-10 hidden lg:block">
                                    <Slider value={volume} onChange={handleVolumeChange} min={0} max={1} step={0.01} sx={{ color: '#EE9322' }} className="p-0" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AudioPlayer;
